import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, Building2, Phone, Mail, MapPin, CreditCard,
  Calendar, Edit, Trash2, Plus, Check, X, RefreshCw,
  FileText, DollarSign, Clock, AlertTriangle, Landmark, User, Pencil,
  History, CalendarDays, Archive, ChevronDown, ChevronUp, Filter, Eye,
  ChevronRight, TrendingDown, TrendingUp, Wallet, Hash, Info, Coins, ScrollText, Search,
  SlidersHorizontal, RotateCcw
} from 'lucide-react';
import { API_URL } from '../config/api';
import PriceInput from '../components/PriceInput';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import './WholesalerDetail.css';
import './WholesalerPayments.css';

const WholesalerDetail = ({ user, selectedStore }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [wholesaler, setWholesaler] = useState(null);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentTab, setPaymentTab] = useState('all');
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [showInfoSection, setShowInfoSection] = useState(false);
  // Ödeme arama ve filtre
  const [paymentSearch, setPaymentSearch] = useState('');
  const [showPaymentFilters, setShowPaymentFilters] = useState(false);
  const [paymentFilterCurrency, setPaymentFilterCurrency] = useState('all');
  const [paymentFilterMethod, setPaymentFilterMethod] = useState('all');
  const [paymentFilterDateFrom, setPaymentFilterDateFrom] = useState('');
  const [paymentFilterDateTo, setPaymentFilterDateTo] = useState('');
  const [paymentSortBy, setPaymentSortBy] = useState('default');

  const paymentActiveFilterCount = [paymentFilterCurrency, paymentFilterMethod].filter(f => f !== 'all').length + (paymentFilterDateFrom ? 1 : 0) + (paymentFilterDateTo ? 1 : 0);
  const resetPaymentFilters = () => { setPaymentFilterCurrency('all'); setPaymentFilterMethod('all'); setPaymentFilterDateFrom(''); setPaymentFilterDateTo(''); setPaymentSortBy('default'); setPaymentSearch(''); };

  // Kısmi ödeme state
  const [showPartialModal, setShowPartialModal] = useState(false);
  const [partialPayment, setPartialPayment] = useState(null);
  const [partialNewAmount, setPartialNewAmount] = useState('');
  const [partialPaymentMethod, setPartialPaymentMethod] = useState('cash');
  const [paymentHistory, setPaymentHistory] = useState([]);

  const [archiveFilter, setArchiveFilter] = useState({
    year: 'all',
    currency: 'all',
    paymentMethod: 'all'
  });

  // Para birimi bilgileri
  const currencyInfo = {
    TRY: { name: 'Türk Lirası', symbol: '₺', icon: '🇹🇷' },
    USD: { name: 'Amerikan Doları', symbol: '$', icon: '🇺🇸' },
    EUR: { name: 'Euro', symbol: '€', icon: '🇪🇺' },
    GOLD: { name: 'Altın (gram)', symbol: 'gr', icon: '🥇' },
    SILVER: { name: 'Gümüş (gram)', symbol: 'gr', icon: '🥈' }
  };

  // Ödeme form state
  const [paymentForm, setPaymentForm] = useState({
    title: '',
    description: '',
    amount: '',
    currency: 'USD',
    payment_type: 'expense',
    category: 'wholesaler',
    due_date: '',
    reminder_days: 3,
    recurring: false,
    recurring_interval: 'monthly',
    notes: '',
    invoice_issued: false
  });

  // Kategori ve durum isimleri
  const categoryNames = {
    wholesaler: 'Toptancı',
    supplier: 'Tedarikçi',
    importer: 'İthalatçı',
    manufacturer: 'Üretici',
    distributor: 'Distribütör',
    broker: 'Komisyoncu',
    foreign_trade: 'Dış Ticaret',
    other: 'Diğer'
  };

  const statusNames = {
    pending: 'Bekliyor',
    paid: 'Ödendi',
    partial: 'Kısmi Ödeme',
    cancelled: 'İptal',
    overdue: 'Gecikmiş'
  };

  // Ödeme yöntemi bilgileri
  const paymentMethodInfo = {
    cash: { name: 'Nakit', icon: '🧾', color: '#22c55e' },
    card: { name: 'Kart', icon: '💳', color: '#3b82f6' },
    gold: { name: 'Altın', icon: '🥇', color: '#f59e0b' },
    silver: { name: 'Gümüş', icon: '🥈', color: '#94a3b8' },
    bank_transfer: { name: 'Havale/EFT', icon: '🏦', color: '#8b5cf6' },
    other: { name: 'Diğer', icon: '📋', color: '#64748b' }
  };

  // Ödendi onay modal state
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [selectedPaymentForPaid, setSelectedPaymentForPaid] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('cash');

  // Genel onay modalı
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    [showConfirmModal, () => setShowConfirmModal(false)],
    [showPaidModal, () => setShowPaidModal(false)],
    [showPaymentModal, () => setShowPaymentModal(false)],
    [showInfoSection, () => setShowInfoSection(false)],
  ]);

  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  useEffect(() => {
    fetchWholesalerData();
  }, [id]);

  const fetchWholesalerData = async () => {
    try {
      setLoading(true);
      const [wholesalerRes, paymentsRes, summaryRes] = await Promise.all([
        fetch(`${API_URL}/wholesalers/${id}`),
        fetch(`${API_URL}/wholesalers/${id}/payments`),
        fetch(`${API_URL}/wholesalers/${id}/summary`)
      ]);

      const wholesalerData = await wholesalerRes.json();
      const paymentsData = await paymentsRes.json();
      const summaryData = await summaryRes.json();

      if (wholesalerRes.ok) {
        setWholesaler(wholesalerData);
      } else {
        toast.error('Toptancı bulunamadı');
        navigate('/wholesalers');
        return;
      }

      setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      setSummary(summaryData);
    } catch (error) {
      console.error('Veri yükleme hatası:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'TRY') => {
    const info = currencyInfo[currency] || currencyInfo.TRY;
    if (currency === 'GOLD' || currency === 'SILVER') {
      return `${Number(amount || 0).toLocaleString()} ${info.symbol}`;
    }
    return `${info.symbol}${Number(amount || 0).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
  };

  const resetPaymentForm = () => {
    setEditingPayment(null);
    setPaymentForm({
      title: '',
      description: '',
      amount: '',
      currency: 'USD',
      payment_type: 'expense',
      category: 'wholesaler',
      due_date: '',
      reminder_days: 3,
      recurring: false,
      recurring_interval: 'monthly',
      notes: '',
      invoice_issued: false
    });
  };

  const openNewPaymentModal = () => {
    resetPaymentForm();
    setShowPaymentModal(true);
  };

  const openEditPaymentModal = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      title: payment.title || '',
      description: payment.description || '',
      amount: payment.amount || '',
      currency: payment.currency || 'TRY',
      payment_type: payment.payment_type || 'expense',
      category: payment.category || 'wholesaler',
      due_date: payment.due_date ? payment.due_date.split('T')[0] : '',
      reminder_days: payment.reminder_days || 3,
      recurring: payment.recurring === 1 || payment.recurring === true,
      recurring_interval: payment.recurring_interval || 'monthly',
      notes: payment.notes || '',
      invoice_issued: payment.invoice_issued === 1 || payment.invoice_issued === true
    });
    setShowPaymentModal(true);
  };

  const handleSavePayment = async () => {
    if (!paymentForm.title || !paymentForm.amount) {
      toast.warning('Başlık ve tutar zorunludur!');
      return;
    }

    try {
      const url = editingPayment
        ? `${API_URL}/wholesaler-payments/${editingPayment.id}`
        : `${API_URL}/wholesaler-payments`;
      const method = editingPayment ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...paymentForm,
          due_date: paymentForm.due_date || null,
          wholesaler_id: parseInt(id),
          recipient: wholesaler.name,
          is_wholesaler: true,
          created_by: user?.id
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(editingPayment ? 'Ödeme güncellendi!' : 'Ödeme oluşturuldu!');
        logService.log('wholesalers', editingPayment ? 'update' : 'create',
          editingPayment
            ? `Toptancı ödemesi güncellendi: ${paymentForm.title} - ${wholesaler?.name}`
            : `Yeni toptancı ödemesi eklendi: ${paymentForm.title} - ${wholesaler?.name}`, {
          entity_type: 'wholesaler_payment',
          entity_id: editingPayment?.id || data.id,
          status: 'success',
          details: {
            wholesaler_name: wholesaler?.name,
            title: paymentForm.title,
            amount: paymentForm.amount,
            currency: paymentForm.currency,
            due_date: paymentForm.due_date
          }
        });
        setShowPaymentModal(false);
        resetPaymentForm();
        fetchWholesalerData();
      } else {
        toast.error(data.error || 'İşlem başarısız!');
        logService.log('wholesalers', editingPayment ? 'update' : 'create',
          `Toptancı ödemesi ${editingPayment ? 'güncellenemedi' : 'eklenemedi'}: ${paymentForm.title}`, {
          status: 'error',
          details: { error: data.error }
        });
      }
    } catch (error) {
      console.error('Ödeme kayıt hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('wholesalers', editingPayment ? 'update' : 'create',
        `Toptancı ödemesi kayıt hatası: ${paymentForm.title}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  const openPaidModal = (payment) => {
    setSelectedPaymentForPaid(payment);
    setSelectedPaymentMethod(payment.payment_method || 'cash');
    setShowPaidModal(true);
  };

  const handlePaidConfirm = async () => {
    if (!selectedPaymentForPaid) return;

    try {
      const response = await fetch(`${API_URL}/wholesaler-payments/${selectedPaymentForPaid.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'paid',
          paid_amount: selectedPaymentForPaid.amount,
          payment_method: selectedPaymentMethod
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Ödeme "${paymentMethodInfo[selectedPaymentMethod]?.name}" olarak kaydedildi!`);
        logService.log('wholesalers', 'update',
          `Toptancı ödemesi ödendi: ${selectedPaymentForPaid.title} - ${selectedPaymentForPaid.amount} ${selectedPaymentForPaid.currency}`, {
          entity_type: 'wholesaler_payment',
          entity_id: selectedPaymentForPaid.id,
          status: 'success',
          details: {
            wholesaler_name: wholesaler?.name,
            title: selectedPaymentForPaid.title,
            amount: selectedPaymentForPaid.amount,
            currency: selectedPaymentForPaid.currency,
            payment_method: selectedPaymentMethod
          }
        });
        setShowPaidModal(false);
        setSelectedPaymentForPaid(null);
        setSelectedPaymentMethod('cash');
        fetchWholesalerData();
      }
    } catch (error) {
      console.error('Ödeme onay hatası:', error);
      toast.error('İşlem başarısız!');
      logService.log('wholesalers', 'update', `Toptancı ödemesi onay hatası: ${selectedPaymentForPaid?.title}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  const updatePaymentStatus = async (payment, newStatus) => {
    const oldStatus = payment.status;
    try {
      const response = await fetch(`${API_URL}/wholesaler-payments/${payment.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (data.success) {
        toast.success(`Ödeme ${statusNames[newStatus].toLowerCase()} olarak işaretlendi!`);
        logService.log('wholesalers', 'update',
          `Toptancı ödemesi durumu değişti: ${payment.title} - ${statusNames[oldStatus]} → ${statusNames[newStatus]}`, {
          entity_type: 'wholesaler_payment',
          entity_id: payment.id,
          status: 'info',
          details: {
            wholesaler_name: wholesaler?.name,
            title: payment.title,
            old_status: oldStatus,
            new_status: newStatus,
            amount: payment.amount
          }
        });
        fetchWholesalerData();
      }
    } catch (error) {
      console.error('Durum güncelleme hatası:', error);
      toast.error('Güncelleme başarısız!');
      logService.log('wholesalers', 'update', `Toptancı ödemesi durum güncelleme hatası: ${payment.title}`, {
        status: 'error',
        details: { error: error.message }
      });
    }
  };

  const openPartialModal = async (payment) => {
    setPartialPayment(payment);
    setPartialNewAmount('');
    setPartialPaymentMethod(payment.payment_method || 'cash');
    setShowPartialModal(true);
    try {
      const res = await fetch(`${API_URL}/wholesaler-payments/${payment.id}/history`);
      const data = await res.json();
      setPaymentHistory(Array.isArray(data) ? data : []);
    } catch (e) {
      setPaymentHistory([]);
    }
  };

  const handlePartialPayment = async () => {
    if (!partialPayment || !partialNewAmount) return;
    const prevPaid = Number(partialPayment.paid_amount) || 0;
    const newPay = parseFloat(partialNewAmount);
    const totalAmount = parseFloat(partialPayment.amount);
    const totalPaid = prevPaid + newPay;
    if (newPay <= 0 || totalPaid > totalAmount) {
      toast.warning('Geçersiz tutar!');
      return;
    }
    try {
      const newStatus = totalPaid >= totalAmount ? 'paid' : 'partial';
      const response = await fetch(`${API_URL}/wholesaler-payments/${partialPayment.id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus, paid_amount: totalPaid, partial_amount: newPay, payment_method: partialPaymentMethod })
      });
      if (response.ok) {
        toast.success(newStatus === 'paid' ? 'Ödeme tamamlandı!' : 'Kısmi ödeme kaydedildi!');
        setShowPartialModal(false);
        setPartialPayment(null);
        setPartialNewAmount('');
        fetchWholesalerData();
      }
    } catch (error) {
      console.error('Kısmi ödeme hatası:', error);
      toast.error('Ödeme kaydedilemedi');
    }
  };

  const handleArchivePayment = async (paymentId) => {
    try {
      const response = await fetch(`${API_URL}/wholesaler-payments/${paymentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived: true })
      });
      if (response.ok) {
        toast.success('Ödeme arşivlendi');
        fetchWholesalerData();
      }
    } catch (error) {
      console.error('Arşivleme hatası:', error);
      toast.error('Arşivleme başarısız');
    }
  };

  const handleDeletePayment = async (payment) => {
    showConfirmation({
      title: 'Ödeme Sil',
      message: `"${payment.title}" ödemesini silmek istediğinize emin misiniz?`,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        try {
          const response = await fetch(`${API_URL}/wholesaler-payments/${payment.id}`, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            toast.success('Ödeme silindi!');
            logService.log('wholesalers', 'delete', `Toptancı ödemesi silindi: ${payment.title} - ${payment.amount} ${payment.currency}`, {
              entity_type: 'wholesaler_payment',
              entity_id: payment.id,
              status: 'warning',
              details: {
                wholesaler_name: wholesaler?.name,
                title: payment.title,
                amount: payment.amount,
                currency: payment.currency,
                due_date: payment.due_date
              }
            });
            fetchWholesalerData();
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Silme başarısız!');
          logService.log('wholesalers', 'delete', `Toptancı ödemesi silme hatası: ${payment.title}`, {
            status: 'error',
            details: { error: error.message }
          });
        }
      }
    });
  };

  // Geçmiş ödemeler
  const getPastPayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return payments.filter(p => {
      if (p.status === 'paid') return true;
      if (!p.due_date) return false;
      const dueDate = new Date(p.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }).sort((a, b) => new Date(b.created_at || b.due_date || 0) - new Date(a.created_at || a.due_date || 0));
  };

  // Gelecek ödemeler
  const getFuturePayments = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return payments.filter(p => {
      if (p.status !== 'pending' && p.status !== 'partial') return false;
      if (!p.due_date) return false;
      const dueDate = new Date(p.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate >= today;
    }).sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Arşiv için yıl listesi
  const getYearsList = () => {
    const years = new Set();
    payments.forEach(p => {
      if (p.due_date) {
        years.add(new Date(p.due_date).getFullYear());
      } else if (p.created_at) {
        years.add(new Date(p.created_at).getFullYear());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  // Filtrelenmiş arşiv ödemeleri
  const getArchivedPayments = () => {
    let filtered = [...payments];

    if (archiveFilter.year !== 'all') {
      filtered = filtered.filter(p => {
        const date = p.due_date || p.created_at;
        return date && new Date(date).getFullYear() === parseInt(archiveFilter.year);
      });
    }
    if (archiveFilter.currency !== 'all') {
      filtered = filtered.filter(p => p.currency === archiveFilter.currency);
    }
    if (archiveFilter.paymentMethod !== 'all') {
      filtered = filtered.filter(p => p.payment_method === archiveFilter.paymentMethod);
    }

    return filtered.sort((a, b) => new Date(b.created_at || b.due_date || 0) - new Date(a.created_at || a.due_date || 0));
  };

  const filteredPayments = payments.filter(p => {
    if (paymentTab === 'all') { if (p.archived) return false; }
    else if (paymentTab === 'pending') { if (p.status !== 'pending' && p.status !== 'partial') return false; }
    else if (paymentTab === 'paid') { if (p.status !== 'paid') return false; }
    else if (paymentTab === 'future') { if (!getFuturePayments().includes(p)) return false; }
    else if (paymentTab === 'archive') { if (!getArchivedPayments().includes(p)) return false; }
    // Arama filtresi
    if (paymentSearch) {
      const q = paymentSearch.toLowerCase();
      if (!(p.title || '').toLowerCase().includes(q) && !(p.description || '').toLowerCase().includes(q) && !(p.notes || '').toLowerCase().includes(q)) return false;
    }
    // Para birimi filtresi
    if (paymentFilterCurrency !== 'all' && p.currency !== paymentFilterCurrency) return false;
    // Ödeme yöntemi filtresi
    if (paymentFilterMethod !== 'all' && p.payment_method !== paymentFilterMethod) return false;
    // Tarih filtresi
    if (paymentFilterDateFrom) { const d = new Date(p.created_at || p.due_date); if (d < new Date(paymentFilterDateFrom)) return false; }
    if (paymentFilterDateTo) { const d = new Date(p.created_at || p.due_date); if (d > new Date(paymentFilterDateTo + 'T23:59:59')) return false; }
    return true;
  }).sort((a, b) => {
    if (paymentSortBy === 'amount_desc') return (Number(b.amount) || 0) - (Number(a.amount) || 0);
    if (paymentSortBy === 'amount_asc') return (Number(a.amount) || 0) - (Number(b.amount) || 0);
    if (paymentSortBy === 'title') return (a.title || '').localeCompare(b.title || '', 'tr');
    // Default: ödenmişler alta, yeniden eskiye
    if (a.status === 'paid' && b.status !== 'paid') return 1;
    if (a.status !== 'paid' && b.status === 'paid') return -1;
    return new Date(b.created_at || b.due_date || 0) - new Date(a.created_at || a.due_date || 0);
  });

  // Tab counts
  const tabCounts = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const pending = payments.filter(p => p.status === 'pending' || p.status === 'partial').length;
    const paid = payments.filter(p => p.status === 'paid').length;
    const past = getPastPayments().length;
    const future = getFuturePayments().length;
    const overdue = payments.filter(p => {
      if (p.status !== 'pending') return false;
      if (!p.due_date) return false;
      const dueDate = new Date(p.due_date);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < today;
    }).length;

    const all = payments.filter(p => !p.archived).length;
    return { all, pending, paid, past, future, overdue, total: payments.length };
  }, [payments]);

  // Ödeme yöntemi dağılımı
  const methodDistribution = useMemo(() => {
    const dist = {};
    payments.filter(p => p.status === 'paid' && p.payment_method).forEach(p => {
      if (!dist[p.payment_method]) {
        dist[p.payment_method] = { count: 0, total: 0, currencies: {} };
      }
      dist[p.payment_method].count++;
      dist[p.payment_method].total += Number(p.amount);
      if (!dist[p.payment_method].currencies[p.currency]) {
        dist[p.payment_method].currencies[p.currency] = 0;
      }
      dist[p.payment_method].currencies[p.currency] += Number(p.amount);
    });
    return dist;
  }, [payments]);

  if (loading) {
    return (
      <div className="wd-container">
        <div className="wd-loading">
          <div className="wd-loading-spinner"></div>
          <p>Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (!wholesaler) {
    return (
      <div className="wd-container">
        <div className="wd-not-found">
          <Building2 size={48} />
          <p>Toptancı bulunamadı</p>
          <button className="wd-btn primary" onClick={() => navigate('/wholesalers')}>
            <ArrowLeft size={16} /> Geri Dön
          </button>
        </div>
      </div>
    );
  }

  const initials = (wholesaler.name || '').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="wd-container">
      {/* Header */}
      <header className="wd-header">
        <div className="wd-header-left">
          <button className="wd-back-btn" onClick={() => navigate('/wholesalers')}>
            <ArrowLeft size={18} /> Toptancılar
          </button>
        </div>
        <div className="wd-header-center">
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Logo"
            className="wd-logo"
          />
        </div>
        <div className="wd-header-right">
          <button className="wd-header-btn" onClick={() => navigate('/wholesaler-payments')} style={{ background: 'white', color: '#0f766e', border: '1px solid #0f766e' }}>
            <DollarSign size={16} /> Ödemeler Sayfası
          </button>
          <button className="wd-header-btn primary" onClick={openNewPaymentModal}>
            <Plus size={18} /> Yeni Ödeme
          </button>
        </div>
      </header>

      {/* Profile Hero */}
      <div className="wd-profile-hero">
        <div className="wd-profile-top">
          <div className="wd-profile-left">
            <div className={`wd-avatar ${!wholesaler.active ? 'inactive' : ''}`}>{initials}</div>
            <div className="wd-profile-info">
              <div className="wd-profile-name-row">
                <h1 className="wd-profile-name">{wholesaler.name}</h1>
                <span className={`wd-status-badge ${wholesaler.active ? 'active' : 'inactive'}`}>
                  {wholesaler.active ? 'Aktif' : 'Pasif'}
                </span>
              </div>
              {wholesaler.company_name && <span className="wd-profile-company">{wholesaler.company_name}</span>}
              {wholesaler.code && <span className="wd-profile-code"><Hash size={12} /> {wholesaler.code}</span>}
            </div>
            {/* Toplam ödenen - ismin yanında */}
            {(() => {
              const paidSummary = {};
              payments.forEach(p => {
                const curr = p.currency || 'TRY';
                const amt = Number(p.amount) || 0;
                const paidAmt = p.status === 'paid' ? amt : (Number(p.paid_amount) || 0);
                if (paidAmt > 0) {
                  if (!paidSummary[curr]) paidSummary[curr] = { paid: 0, total: 0 };
                  paidSummary[curr].paid += paidAmt;
                  paidSummary[curr].total += amt;
                }
              });
              const currencies = Object.keys(paidSummary);
              if (currencies.length === 0) return (
                <div style={{ marginLeft: '8px', padding: '8px 14px', background: '#f8fafc', borderRadius: '10px', border: '1px dashed #cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <DollarSign size={14} color="#94a3b8" />
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>Henüz ödeme kaydı yok</span>
                </div>
              );
              return (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '8px' }}>
                  {currencies.map(curr => {
                    const info = currencyInfo[curr] || currencyInfo.TRY;
                    const s = paidSummary[curr];
                    const isGr = curr === 'GOLD' || curr === 'SILVER';
                    const label = isGr ? (curr === 'GOLD' ? ' gr Altın' : ' gr Gümüş') : '';
                    const fmtVal = isGr ? `${s.paid.toLocaleString('tr-TR')}${label}` : `${info.symbol}${s.paid.toLocaleString('tr-TR')}`;
                    const fmtTotal = isGr ? `${s.total.toLocaleString('tr-TR')}${label}` : `${info.symbol}${s.total.toLocaleString('tr-TR')}`;
                    return (
                      <div key={curr} style={{ padding: '8px 14px', background: '#f0fdf4', borderRadius: '10px', border: '1.5px solid #86efac', textAlign: 'center', boxShadow: '0 1px 4px rgba(34,197,94,0.1)' }}>
                        <div style={{ fontSize: '10px', color: '#15803d', fontWeight: 700, letterSpacing: '0.3px' }}>{info.icon} Ödenen</div>
                        <div style={{ fontSize: '18px', fontWeight: 800, color: '#16a34a', marginTop: '2px' }}>{fmtVal}</div>
                        <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px' }}>toplam: {fmtTotal}</div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          <div className="wd-profile-right">
            <button className="wd-edit-btn" onClick={() => navigate('/wholesalers', { state: { editId: wholesaler.id } })}>
              <Edit size={16} /> Düzenle
            </button>
          </div>
        </div>

      </div>

      {/* Financial Dashboard & Payment Method Distribution removed */}

      {/* Payment Tabs + List */}
      <div className="wd-payments-section">
        <div className="wp-tabs-filter-container">
          <div className="wp-tabs-row">
            <div className="wp-tabs">
              {[
                { id: 'all', label: 'Ödemeler', count: tabCounts.all },
                { id: 'past', label: 'Geçmiş Ödemeler', count: tabCounts.past },
                { id: 'future', label: 'Gelecek Ödemeler', count: tabCounts.future },
                { id: 'pending', label: 'Bekleyen', count: tabCounts.pending },
                { id: 'paid', label: 'Tamamlanan', count: tabCounts.paid, color: 'green' },
                { id: 'archive', label: 'Arşiv' },
                { id: 'report', label: 'Rapor' },
              ].map(tab => (
                <button key={tab.id} className={`wp-tab ${paymentTab === tab.id ? 'active' : ''} ${tab.color || ''}`} onClick={() => setPaymentTab(tab.id)}>
                  <span className="wp-tab-label">{tab.label}</span>
                  {tab.count !== undefined && <span className="wp-tab-count">{tab.count}</span>}
                </button>
              ))}
            </div>

            {paymentTab !== 'report' && (
              <>
                <div className="wp-search">
                  <Search size={16} />
                  <input type="text" placeholder="Ödeme ara..." value={paymentSearch} onChange={e => setPaymentSearch(e.target.value)} />
                  {paymentSearch && <button className="wp-search-clear" onClick={() => setPaymentSearch('')}>×</button>}
                </div>
                <button className={`wp-filter-toggle ${showPaymentFilters ? 'active' : ''} ${paymentActiveFilterCount > 0 ? 'has-filters' : ''}`} onClick={() => setShowPaymentFilters(!showPaymentFilters)}>
                  <SlidersHorizontal size={15} />
                  <span>Filtre</span>
                  {paymentActiveFilterCount > 0 && <span className="wp-filter-toggle-count">{paymentActiveFilterCount}</span>}
                  {showPaymentFilters ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
              </>
            )}
          </div>

          {showPaymentFilters && paymentTab !== 'report' && (
            <div className="wp-filters-panel">
              <div className="wp-filters-grid">
                <div className="wp-filter-group">
                  <label>Para Birimi</label>
                  <select value={paymentFilterCurrency} onChange={e => setPaymentFilterCurrency(e.target.value)}>
                    <option value="all">Tümü</option>
                    {Object.entries(currencyInfo).map(([code, info]) => (
                      <option key={code} value={code}>{info.icon} {info.name}</option>
                    ))}
                  </select>
                </div>
                <div className="wp-filter-group">
                  <label>Ödeme Yöntemi</label>
                  <select value={paymentFilterMethod} onChange={e => setPaymentFilterMethod(e.target.value)}>
                    <option value="all">Tümü</option>
                    {Object.entries(paymentMethodInfo).map(([key, info]) => (
                      <option key={key} value={key}>{info.icon} {info.name}</option>
                    ))}
                  </select>
                </div>
                <div className="wp-filter-group">
                  <label>Tarih Aralığı</label>
                  <div className="wp-filter-date-range">
                    <input type="date" value={paymentFilterDateFrom} onChange={e => setPaymentFilterDateFrom(e.target.value)} />
                    <span>—</span>
                    <input type="date" value={paymentFilterDateTo} onChange={e => setPaymentFilterDateTo(e.target.value)} />
                  </div>
                </div>
                <div className="wp-filter-group">
                  <label>Sıralama</label>
                  <select value={paymentSortBy} onChange={e => setPaymentSortBy(e.target.value)}>
                    <option value="default">Varsayılan</option>
                    <option value="amount_desc">Tutar (Büyük→Küçük)</option>
                    <option value="amount_asc">Tutar (Küçük→Büyük)</option>
                    <option value="title">Başlık (A→Z)</option>
                  </select>
                </div>
              </div>
              {paymentActiveFilterCount > 0 && (
                <button className="wp-filters-reset" onClick={resetPaymentFilters}>
                  <RotateCcw size={13} /> Filtreleri Temizle
                </button>
              )}
            </div>
          )}
        </div>

        {/* Archive Filters */}
        {paymentTab === 'archive' && (
          <div className="wd-archive-filters">
            <div className="wd-filter-group">
              <label><Filter size={13} /> Yıl</label>
              <select value={archiveFilter.year} onChange={(e) => setArchiveFilter({...archiveFilter, year: e.target.value})}>
                <option value="all">Tümü</option>
                {getYearsList().map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
            <div className="wd-filter-group">
              <label>Para Birimi</label>
              <select value={archiveFilter.currency} onChange={(e) => setArchiveFilter({...archiveFilter, currency: e.target.value})}>
                <option value="all">Tümü</option>
                {Object.entries(currencyInfo).map(([code, info]) => (
                  <option key={code} value={code}>{info.icon} {code}</option>
                ))}
              </select>
            </div>
            <div className="wd-filter-group">
              <label>Ödeme Yöntemi</label>
              <select value={archiveFilter.paymentMethod} onChange={(e) => setArchiveFilter({...archiveFilter, paymentMethod: e.target.value})}>
                <option value="all">Tümü</option>
                {Object.entries(paymentMethodInfo).map(([code, info]) => (
                  <option key={code} value={code}>{info.icon} {info.name}</option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Rapor Tab */}
        {paymentTab === 'report' && (() => {
          const allP = payments; // Raporda arşiv dahil tüm ödemeler
          const currSummary = {};
          allP.forEach(p => {
            const curr = p.currency || 'TRY';
            const amt = Number(p.amount) || 0;
            const paidAmt = p.status === 'paid' ? amt : (Number(p.paid_amount) || 0);
            if (!currSummary[curr]) currSummary[curr] = { total: 0, paid: 0, pending: 0, count: 0 };
            currSummary[curr].total += amt;
            currSummary[curr].paid += paidAmt;
            currSummary[curr].pending += Math.max(0, amt - paidAmt);
            currSummary[curr].count++;
          });
          const currencies = Object.keys(currSummary);

          // Tüm ödemeleri tarihe göre sırala (yeniden eskiye)
          const sortedPayments = [...allP].sort((a, b) => new Date(b.created_at || b.due_date || 0) - new Date(a.created_at || a.due_date || 0));

          return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            {/* Üst Özet - Para Birimi Bazlı */}
            {currencies.map(curr => {
              const s = currSummary[curr];
              const info = currencyInfo[curr] || currencyInfo.TRY;
              const pct = s.total > 0 ? Math.round((s.paid / s.total) * 100) : 0;
              const isGram = curr === 'GOLD' || curr === 'SILVER';
              const gramLabel = curr === 'GOLD' ? 'Altın' : curr === 'SILVER' ? 'Gümüş' : '';
              const fmtVal = (val) => isGram ? `${val.toLocaleString('tr-TR')} gr ${gramLabel}` : `${info.symbol}${val.toLocaleString('tr-TR')}`;
              return (
                <div key={curr} style={{ display: 'flex', alignItems: 'center', gap: '0', background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                  <div style={{ flex: 1, padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '24px' }}>{info.icon}</span>
                    <div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>TOPLAM BORÇ</div>
                      <div style={{ fontSize: '22px', fontWeight: 800, color: '#dc2626' }}>{fmtVal(s.total)}</div>
                    </div>
                  </div>
                  <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0' }}></div>
                  <div style={{ flex: 1, padding: '14px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>TOPLAM ÖDENEN</div>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: '#16a34a' }}>{fmtVal(s.paid)}</div>
                  </div>
                  <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0' }}></div>
                  <div style={{ flex: 1, padding: '14px 20px', textAlign: 'center' }}>
                    <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>KALAN</div>
                    <div style={{ fontSize: '22px', fontWeight: 800, color: s.pending > 0 ? '#d97706' : '#16a34a' }}>{fmtVal(s.pending)}</div>
                  </div>
                  <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0' }}></div>
                  <div style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '8px', minWidth: '100px' }}>
                    <div style={{ position: 'relative', width: '44px', height: '44px', flexShrink: 0 }}>
                      <svg viewBox="0 0 36 36" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                        <circle cx="18" cy="18" r="15.9" fill="none" stroke="#22c55e" strokeWidth="3" strokeDasharray={`${pct} ${100 - pct}`} strokeLinecap="round" />
                      </svg>
                      <span style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', fontSize: '10px', fontWeight: 800, color: '#1e293b' }}>%{pct}</span>
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Ödeme Geçmişi - Excel Tablo */}
            <div style={{ background: 'white', borderRadius: '10px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '11px' }}>#</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '11px' }}>Tarih</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '11px' }}>Açıklama</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: '11px' }}>Tür</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#dc2626', fontSize: '11px' }}>Tutar</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#16a34a', fontSize: '11px' }}>Ödenen</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontWeight: 700, color: '#d97706', fontSize: '11px' }}>Kalan</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', fontWeight: 700, color: '#475569', fontSize: '11px' }}>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPayments.length === 0 ? (
                    <tr><td colSpan={8} style={{ padding: '20px', textAlign: 'center', color: '#94a3b8' }}>Ödeme kaydı yok</td></tr>
                  ) : (
                    sortedPayments.map((p, i) => {
                      const amt = Number(p.amount) || 0;
                      const paidAmt = p.status === 'paid' ? amt : (Number(p.paid_amount) || 0);
                      const remaining = Math.max(0, amt - paidAmt);
                      const curr = p.currency || 'TRY';
                      const info = currencyInfo[curr] || currencyInfo.TRY;
                      const isGr = curr === 'GOLD' || curr === 'SILVER';
                      const grLabel = curr === 'GOLD' ? ' gr Altın' : curr === 'SILVER' ? ' gr Gümüş' : '';
                      const fmt = (v) => isGr ? `${v.toLocaleString('tr-TR')}${grLabel}` : `${info.symbol}${v.toLocaleString('tr-TR')}`;
                      const date = new Date(p.created_at || p.due_date || Date.now());
                      const isEven = i % 2 === 0;
                      return (
                        <tr key={p.id} style={{ background: isEven ? 'white' : '#fafbfc', borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 14px', color: '#94a3b8', fontSize: '11px' }}>{i + 1}</td>
                          <td style={{ padding: '8px 14px', color: '#64748b', whiteSpace: 'nowrap' }}>{date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</td>
                          <td style={{ padding: '8px 14px', color: '#1e293b', fontWeight: 500 }}>{p.title}</td>
                          <td style={{ padding: '8px 14px' }}>
                            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px', fontWeight: 600, background: p.payment_type === 'expense' ? '#fef2f2' : '#f0fdf4', color: p.payment_type === 'expense' ? '#dc2626' : '#16a34a' }}>
                              {p.payment_type === 'expense' ? 'Borç' : 'Alacak'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmt(amt)}</td>
                          <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: '#16a34a' }}>{paidAmt > 0 ? fmt(paidAmt) : '—'}</td>
                          <td style={{ padding: '8px 14px', textAlign: 'right', fontWeight: 700, color: remaining > 0 ? '#d97706' : '#16a34a' }}>{fmt(remaining)}</td>
                          <td style={{ padding: '8px 14px', textAlign: 'center' }}>
                            <span className={`wp-status-badge ${p.status}`} style={{ fontSize: '10px', padding: '2px 8px' }}>{statusNames[p.status]}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
          );
        })()}

        {/* Payments List */}
        {paymentTab !== 'report' && (
        <div className="wd-payments-list" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {/* Başlık satırı - WholesalerPayments ile aynı */}
          {filteredPayments.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2px', background: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
              <div style={{ flex: 1, padding: '10px 16px', fontSize: '12px', fontWeight: 700, color: '#475569' }}>Ödeme Başlığı</div>
              <div style={{ width: '80px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: '#94a3b8' }}>Ekleme</div>
              <div style={{ width: '80px', textAlign: 'center', fontSize: '10px', fontWeight: 700, color: '#94a3b8' }}>Son Ödeme</div>
              <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0' }}></div>
              <div style={{ display: 'flex', alignItems: 'center', padding: '10px 12px' }}>
                <div style={{ width: '130px', textAlign: 'center', fontSize: '12px', fontWeight: 700, color: '#dc2626' }}>Kalan Borç</div>
                <div style={{ width: '1px', height: '16px', background: '#e2e8f0', margin: '0 6px' }}></div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span style={{ width: '44px', textAlign: 'center', padding: '3px 0', fontSize: '10px', fontWeight: 700, color: '#3b82f6', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }}>Kısmi</span>
                  <span style={{ width: '44px', textAlign: 'center', padding: '3px 0', fontSize: '10px', fontWeight: 700, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '6px' }}>Ödendi</span>
                  <span style={{ width: '44px', textAlign: 'center', padding: '3px 0', fontSize: '10px', fontWeight: 700, color: '#8b5cf6', background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: '6px' }}>Arşiv</span>
                  <span style={{ width: '44px', textAlign: 'center', padding: '3px 0', fontSize: '10px', fontWeight: 700, color: '#475569', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: '6px' }}>Düzenle</span>
                  <span style={{ width: '44px', textAlign: 'center', padding: '3px 0', fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px' }}>Sil</span>
                </div>
              </div>
            </div>
          )}
          {filteredPayments.length === 0 ? (
            <div className="wd-no-payments">
              <Clock size={32} />
              <p>Bu kategoride ödeme bulunmuyor</p>
            </div>
          ) : (
            filteredPayments.map((payment, index) => {
              const daysUntil = getDaysUntilDue(payment.due_date);
              const isOverdue = payment.status === 'pending' && daysUntil !== null && daysUntil < 0;
              const isExpanded = expandedPayment === payment.id;

              {
                const amt = Number(payment.amount) || 0;
                const isPaidFull = payment.status === 'paid';
                const paidAmt = isPaidFull ? amt : (Number(payment.paid_amount) || 0);
                const remaining = Math.max(0, amt - paidAmt);
                const curr = payment.currency || 'TRY';
                const cInfo = currencyInfo[curr] || currencyInfo.TRY;
                const isGr = curr === 'GOLD' || curr === 'SILVER';
                const grLabel = isGr ? (curr === 'GOLD' ? ' gr Altın' : ' gr Gümüş') : '';
                const fmt = (v) => isGr ? `${v.toLocaleString('tr-TR')}${grLabel}` : `${cInfo.symbol}${v.toLocaleString('tr-TR')}`;
                const date = new Date(payment.created_at || payment.due_date || Date.now());

                return (
                <div key={payment.id} className={`wp-payment-card ${payment.status} ${payment.payment_type} ${isOverdue ? 'overdue' : ''}`}
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '0' }}
                  onClick={() => setExpandedPayment(expandedPayment === payment.id ? null : payment.id)}
                >
                  {/* SOL: Başlık + tarih + durum */}
                  <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 16px' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: '14px', fontWeight: 700, color: isPaidFull ? '#94a3b8' : '#1e293b', textDecoration: isPaidFull ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {payment.title}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '2px' }}>
                        {payment.description || payment.notes || ''}
                      </div>
                    </div>
                    <span className={`wp-status-badge ${payment.status}`} style={{ fontSize: '10px', padding: '2px 8px', flexShrink: 0 }}>
                      {statusNames[payment.status]}
                    </span>
                  </div>

                  {/* Ekleme tarihi */}
                  <div style={{ width: '80px', textAlign: 'center', fontSize: '11px', color: '#94a3b8', flexShrink: 0 }}>
                    {date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                  </div>

                  {/* Son ödeme tarihi */}
                  <div style={{ width: '80px', textAlign: 'center', fontSize: '11px', color: '#64748b', flexShrink: 0 }}>
                    {payment.last_payment_date ? new Date(payment.last_payment_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : payment.paid_date ? new Date(payment.paid_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—'}
                  </div>

                  {/* Dikey çizgi */}
                  <div style={{ width: '1px', alignSelf: 'stretch', background: '#e2e8f0', flexShrink: 0 }}></div>

                  {/* SAĞ: Kalan + aksiyonlar */}
                  <div style={{ display: 'flex', alignItems: 'center', padding: '8px 12px', flexShrink: 0 }}>
                    {/* Kalan borç kutusu - tıklayınca kısmi ödeme */}
                    <div style={{ width: '130px', display: 'flex', justifyContent: 'center' }} onClick={e => { e.stopPropagation(); if (!isPaidFull && remaining > 0) openPartialModal(payment); }}>
                      <div style={{ padding: '6px 16px', background: isPaidFull ? '#f1f5f9' : remaining > 0 ? '#fef2f2' : '#f0fdf4', borderRadius: '8px', border: `1px solid ${isPaidFull ? '#e2e8f0' : remaining > 0 ? '#fecaca' : '#bbf7d0'}`, textAlign: 'center', minWidth: '80px', cursor: !isPaidFull && remaining > 0 ? 'pointer' : 'default' }}>
                        <div style={{ fontSize: '15px', fontWeight: 800, color: isPaidFull ? '#a3a3a3' : remaining > 0 ? '#dc2626' : '#16a34a', textDecoration: isPaidFull ? 'line-through' : 'none' }}>
                          {fmt(remaining)}
                        </div>
                      </div>
                    </div>

                    <div style={{ width: '1px', height: '28px', background: '#e2e8f0', margin: '0 6px', flexShrink: 0 }}></div>

                    {/* Aksiyon butonları - WholesalerPayments ile aynı */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }} onClick={e => e.stopPropagation()}>
                      {/* Kısmi */}
                      <div style={{ width: '44px', display: 'flex', justifyContent: 'center' }}>
                        {(payment.status === 'pending' || payment.status === 'partial') ? (
                          <button className="wp-row-icon-btn" onClick={() => openPartialModal(payment)} title="Kısmi Ödeme"><Coins size={16} color="#3b82f6" /></button>
                        ) : (
                          <span style={{ color: '#e2e8f0' }}>—</span>
                        )}
                      </div>
                      {/* Ödendi */}
                      <div style={{ width: '44px', display: 'flex', justifyContent: 'center' }}>
                        {(payment.status === 'pending' || payment.status === 'partial') ? (
                          <button className="wp-row-icon-btn" onClick={() => openPaidModal(payment)} title="Ödendi"><Check size={16} color="#22c55e" /></button>
                        ) : (
                          <span style={{ color: '#e2e8f0' }}>—</span>
                        )}
                      </div>
                      {/* Arşiv */}
                      <div style={{ width: '44px', display: 'flex', justifyContent: 'center' }}>
                        {isPaidFull ? (
                          <button className="wp-row-icon-btn" onClick={() => handleArchivePayment(payment.id)} title="Arşivle"><ScrollText size={16} color="#8b5cf6" /></button>
                        ) : (
                          <span style={{ color: '#e2e8f0' }}>—</span>
                        )}
                      </div>
                      {/* Düzenle */}
                      <div style={{ width: '44px', display: 'flex', justifyContent: 'center' }}>
                        <button className="wp-row-icon-btn" onClick={() => openEditPaymentModal(payment)} title="Düzenle"><Pencil size={15} color="#64748b" /></button>
                      </div>
                      {/* Sil */}
                      <div style={{ width: '44px', display: 'flex', justifyContent: 'center' }}>
                        <button className="wp-row-icon-btn" onClick={() => handleDeletePayment(payment)} title="Sil"><Trash2 size={15} color="#ef4444" /></button>
                      </div>
                    </div>
                  </div>
                </div>
                );
              }
            })
          )}
        </div>
        )}
      </div>

      {/* Kısmi Ödeme Modal - WholesalerPayments ile aynı */}
      {showPartialModal && partialPayment && (
        <div className="wp-modal-overlay" onClick={() => setShowPartialModal(false)}>
          <div className="wp-modal partial" onClick={e => e.stopPropagation()}>
            <div className="wp-modal-header">
              <div className="wp-modal-header-info">
                <h2>Kısmi Ödeme</h2>
                <span className="wp-modal-subtitle">{partialPayment.title} · {wholesaler?.name}</span>
              </div>
              <button className="wp-modal-close" onClick={() => setShowPartialModal(false)}>×</button>
            </div>
            <div className="wp-modal-body">
              {(() => {
                const totalAmt = Number(partialPayment.amount) || 0;
                const prevPaid = Number(partialPayment.paid_amount) || 0;
                const newPay = Number(partialNewAmount) || 0;
                const totalPaid = prevPaid + newPay;
                const liveRemaining = Math.max(0, totalAmt - totalPaid);
                const livePct = totalAmt > 0 ? Math.min(100, Math.round((totalPaid / totalAmt) * 100)) : 0;
                const isComplete = totalPaid >= totalAmt;
                const remainingDebt = totalAmt - prevPaid;
                const methodLabels = { cash: 'Nakit', credit_card: 'Kredi Kartı', bank_transfer: 'Havale/EFT', check: 'Çek' };
                const curr = partialPayment.currency;
                const historyTotal = paymentHistory.reduce((sum, h) => sum + Number(h.amount), 0);
                const unrecordedAmount = prevPaid - historyTotal;

                return (
                  <div className="wp-ledger">
                    <div className="wp-ledger-title"><span>Hesap Özeti</span></div>
                    <div className="wp-ledger-head">
                      <span className="wp-ledger-col-date">Tarih</span>
                      <span className="wp-ledger-col-desc">Açıklama</span>
                      <span className="wp-ledger-col-invoice">Fatura</span>
                      <span className="wp-ledger-col-amount">Tutar</span>
                      <span className="wp-ledger-col-balance">Bakiye</span>
                    </div>
                    <div className="wp-ledger-body">
                      {(() => {
                        const createdDate = partialPayment.created_at ? new Date(partialPayment.created_at) : new Date(partialPayment.due_date);
                        return (
                          <div className="wp-ledger-row debit">
                            <span className="wp-ledger-col-date">{createdDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                            <span className="wp-ledger-col-desc">Borç kaydı</span>
                            <span className="wp-ledger-col-invoice">
                              <button className={`wp-invoice-check ${partialPayment.invoice_issued ? 'checked' : ''}`}
                                onClick={(e) => { e.stopPropagation(); /* fatura toggle */ }}
                                title={partialPayment.invoice_issued ? 'Fatura kesildi' : 'Fatura kes'}>
                                <FileText size={13} />
                                {partialPayment.invoice_issued ? <Check size={10} className="wp-invoice-tick" /> : null}
                              </button>
                            </span>
                            <span className="wp-ledger-col-amount debit">+{formatCurrency(totalAmt, curr)}</span>
                            <span className="wp-ledger-col-balance">{formatCurrency(totalAmt, curr)}</span>
                          </div>
                        );
                      })()}
                      {unrecordedAmount > 0.01 && (
                        <div className="wp-ledger-row credit old">
                          <span className="wp-ledger-col-date">—</span>
                          <span className="wp-ledger-col-desc">Eski ödeme</span>
                          <span className="wp-ledger-col-invoice"></span>
                          <span className="wp-ledger-col-amount credit">-{formatCurrency(unrecordedAmount, curr)}</span>
                          <span className="wp-ledger-col-balance">{formatCurrency(totalAmt - unrecordedAmount, curr)}</span>
                        </div>
                      )}
                      {[...paymentHistory].reverse().map((h, i) => {
                        const hDate = new Date(h.created_at);
                        const paidUpToHere = (unrecordedAmount > 0.01 ? unrecordedAmount : 0) +
                          [...paymentHistory].reverse().slice(0, i + 1).reduce((s, x) => s + Number(x.amount), 0);
                        const balanceAfter = totalAmt - paidUpToHere;
                        return (
                          <div key={h.id} className="wp-ledger-row credit" title="Düzenlemek için çift tıklayın">
                            <span className="wp-ledger-col-date" title={hDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}>
                              {hDate.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                              <small>{hDate.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</small>
                            </span>
                            <span className="wp-ledger-col-desc">Ödeme <small>({methodLabels[h.payment_method] || h.payment_method})</small></span>
                            <span className="wp-ledger-col-invoice">
                              <button className={`wp-invoice-check ${h.invoice_issued ? 'checked' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  (async () => {
                                    try {
                                      await fetch(`${API_URL}/wholesaler-payment-history/${h.id}/invoice`, {
                                        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ invoice_issued: !h.invoice_issued })
                                      });
                                      setPaymentHistory(prev => prev.map(ph => ph.id === h.id ? { ...ph, invoice_issued: !ph.invoice_issued } : ph));
                                    } catch (err) { console.error(err); }
                                  })();
                                }}>
                                <FileText size={13} />
                                {h.invoice_issued ? <Check size={10} className="wp-invoice-tick" /> : null}
                              </button>
                            </span>
                            <span className="wp-ledger-col-amount credit">-{formatCurrency(h.amount, curr)}</span>
                            <span className="wp-ledger-col-balance">{formatCurrency(Math.max(0, balanceAfter), curr)}</span>
                          </div>
                        );
                      })}
                      {newPay > 0 && (
                        <div className="wp-ledger-row credit new-entry">
                          <span className="wp-ledger-col-date">Yeni</span>
                          <span className="wp-ledger-col-desc">Yeni ödeme</span>
                          <span className="wp-ledger-col-invoice"></span>
                          <span className="wp-ledger-col-amount credit">-{formatCurrency(newPay, curr)}</span>
                          <span className="wp-ledger-col-balance">{formatCurrency(liveRemaining, curr)}</span>
                        </div>
                      )}
                    </div>
                    <div className="wp-ledger-footer">
                      <div className="wp-ledger-footer-row"><span>Toplam Borç</span><strong className="debit">{formatCurrency(totalAmt, curr)}</strong></div>
                      <div className="wp-ledger-footer-row"><span>Toplam Ödenen</span><strong className="credit">{formatCurrency(totalPaid, curr)}</strong></div>
                      <div className={`wp-ledger-footer-row result ${isComplete ? 'complete' : ''}`}><span>Kalan Borç</span><strong>{formatCurrency(liveRemaining, curr)}</strong></div>
                    </div>
                    <div className="wp-ledger-progress">
                      <div className="wp-ledger-progress-bar"><div className="wp-ledger-progress-fill" style={{ width: `${livePct}%` }} /></div>
                      <span className="wp-ledger-progress-pct">%{livePct}</span>
                    </div>
                    <div className="wp-ledger-input-section">
                      <label>Yeni Ödeme Ekle</label>
                      <div className="wp-partial-input">
                        <span>{currencyInfo[curr]?.symbol || '₺'}</span>
                        <PriceInput value={partialNewAmount} onChange={val => setPartialNewAmount(val)} placeholder="0.00" min="0" max={remainingDebt} autoFocus />
                      </div>
                      <div className="wp-quick-amounts">
                        <button onClick={() => setPartialNewAmount(Math.round(remainingDebt * 0.25))}>%25</button>
                        <button onClick={() => setPartialNewAmount(Math.round(remainingDebt * 0.5))}>%50</button>
                        <button onClick={() => setPartialNewAmount(Math.round(remainingDebt * 0.75))}>%75</button>
                        <button onClick={() => setPartialNewAmount(remainingDebt)}>Tamamı</button>
                      </div>
                    </div>
                    {isComplete && <div className="wp-ledger-complete">Ödeme tamamlanacak!</div>}
                  </div>
                );
              })()}
              <div className="wp-form-group">
                <label>Ödeme Yöntemi</label>
                <div className="wp-payment-methods">
                  {Object.entries(paymentMethodInfo).map(([key, info]) => (
                    <button key={key} className={`wp-method-btn ${partialPaymentMethod === key ? 'active' : ''}`} onClick={() => setPartialPaymentMethod(key)}>
                      <span className="wp-method-icon">{info.icon}</span>
                      <span className="wp-method-name">{info.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="wp-modal-footer">
              <button className="wp-btn cancel" onClick={() => setShowPartialModal(false)}>Kapat</button>
              <button className="wp-btn save" onClick={() => {
                if (!partialNewAmount || Number(partialNewAmount) <= 0) { setShowPartialModal(false); fetchWholesalerData(); return; }
                handlePartialPayment();
              }}>
                <Coins size={16} /> {(!partialNewAmount || Number(partialNewAmount) <= 0) ? 'Kaydet ve Kapat' : 'Ödemeyi Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="wd-modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="wd-modal" onClick={e => e.stopPropagation()}>
            <div className="wd-modal-header">
              <h2>{editingPayment ? 'Ödeme Düzenle' : 'Yeni Ödeme'}</h2>
              <button className="wd-modal-close" onClick={() => setShowPaymentModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="wd-modal-body">
              <div className="wd-form-row">
                <div className="wd-form-group">
                  <label>Başlık *</label>
                  <input type="text" value={paymentForm.title} onChange={e => setPaymentForm({...paymentForm, title: e.target.value})} placeholder="Ödeme başlığı..." />
                </div>
                <div className="wd-form-group">
                  <label>Tutar *</label>
                  <div className="wd-amount-row">
                    <PriceInput value={paymentForm.amount} onChange={val => setPaymentForm({...paymentForm, amount: val})} placeholder="0.00" />
                    <select value={paymentForm.currency} onChange={e => setPaymentForm({...paymentForm, currency: e.target.value})}>
                      {Object.entries(currencyInfo).map(([code, info]) => (
                        <option key={code} value={code}>{info.icon} {code}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="wd-form-row">
                <div className="wd-form-group">
                  <label>Tür</label>
                  <div className="wd-type-btns">
                    <button className={`wd-type-btn expense ${paymentForm.payment_type === 'expense' ? 'active' : ''}`} onClick={() => setPaymentForm({...paymentForm, payment_type: 'expense'})}>Borç</button>
                    <button className={`wd-type-btn income ${paymentForm.payment_type === 'income' ? 'active' : ''}`} onClick={() => setPaymentForm({...paymentForm, payment_type: 'income'})}>Alacak</button>
                  </div>
                </div>
                <div className="wd-form-group">
                  <label>Vade Tarihi</label>
                  <input type="date" value={paymentForm.due_date} onChange={e => setPaymentForm({...paymentForm, due_date: e.target.value})} />
                </div>
              </div>

              <div className="wd-form-row">
                <div className="wd-form-group">
                  <label>Kategori</label>
                  <select value={paymentForm.category} onChange={e => setPaymentForm({...paymentForm, category: e.target.value})}>
                    {Object.entries(categoryNames).map(([key, name]) => (
                      <option key={key} value={key}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="wd-form-group">
                  <label>Hatırlatma</label>
                  <select value={paymentForm.reminder_days} onChange={e => setPaymentForm({...paymentForm, reminder_days: Number(e.target.value)})}>
                    <option value={1}>1 gün önce</option>
                    <option value={3}>3 gün önce</option>
                    <option value={7}>1 hafta önce</option>
                    <option value={14}>2 hafta önce</option>
                  </select>
                </div>
              </div>

              <div className="wd-form-row">
                <div className="wd-form-group">
                  <label className="wd-checkbox-label">
                    <input type="checkbox" checked={paymentForm.recurring} onChange={e => setPaymentForm({...paymentForm, recurring: e.target.checked})} />
                    <span>Tekrarlayan Ödeme</span>
                  </label>
                </div>
                {paymentForm.recurring && (
                  <div className="wd-form-group">
                    <label>Tekrar Sıklığı</label>
                    <select value={paymentForm.recurring_interval} onChange={e => setPaymentForm({...paymentForm, recurring_interval: e.target.value})}>
                      <option value="weekly">Haftalık</option>
                      <option value="monthly">Aylık</option>
                      <option value="yearly">Yıllık</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="wd-form-group">
                <label>Açıklama</label>
                <textarea value={paymentForm.description} onChange={e => setPaymentForm({...paymentForm, description: e.target.value})} placeholder="Ödeme detayları..." rows="2" />
              </div>

              <div className="wd-form-group">
                <label>Notlar</label>
                <textarea value={paymentForm.notes} onChange={e => setPaymentForm({...paymentForm, notes: e.target.value})} placeholder="Ek notlar..." rows="2" />
              </div>

              <div className="wd-form-group">
                <label className="wd-checkbox-label invoice">
                  <input type="checkbox" checked={paymentForm.invoice_issued} onChange={e => setPaymentForm({...paymentForm, invoice_issued: e.target.checked})} />
                  <FileText size={16} style={{ color: paymentForm.invoice_issued ? '#22c55e' : '#94a3b8' }} />
                  <span>Fatura Kesildi</span>
                </label>
              </div>
            </div>
            <div className="wd-modal-footer">
              <button className="wd-btn cancel" onClick={() => setShowPaymentModal(false)}>İptal</button>
              <button className="wd-btn primary" onClick={handleSavePayment}>
                <Check size={16} /> {editingPayment ? 'Güncelle' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ödendi Onay Modal */}
      {showPaidModal && selectedPaymentForPaid && (
        <div className="wd-modal-overlay" onClick={() => setShowPaidModal(false)}>
          <div className="wd-modal small" onClick={e => e.stopPropagation()}>
            <div className="wd-modal-header">
              <h2>Ödeme Onayı</h2>
              <button className="wd-modal-close" onClick={() => setShowPaidModal(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="wd-modal-body">
              <div className="wd-paid-confirm-info">
                <h3>{selectedPaymentForPaid.title}</h3>
                <p>{wholesaler.name}</p>
                <span className="wd-paid-amount">{formatCurrency(selectedPaymentForPaid.amount, selectedPaymentForPaid.currency)}</span>
              </div>

              <div className="wd-form-group">
                <label>Ödeme Yöntemi Seçin</label>
                <div className="wd-method-select-grid">
                  {Object.entries(paymentMethodInfo).map(([key, info]) => (
                    <button
                      key={key}
                      className={`wd-method-select-btn ${selectedPaymentMethod === key ? 'active' : ''}`}
                      onClick={() => setSelectedPaymentMethod(key)}
                      style={{ borderColor: selectedPaymentMethod === key ? info.color : '#e2e8f0', backgroundColor: selectedPaymentMethod === key ? info.color + '10' : 'white' }}
                    >
                      <span className="wd-method-btn-icon">{info.icon}</span>
                      <span className="wd-method-btn-name" style={{ color: selectedPaymentMethod === key ? info.color : '#64748b' }}>{info.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="wd-modal-footer">
              <button className="wd-btn cancel" onClick={() => setShowPaidModal(false)}>İptal</button>
              <button className="wd-btn primary" onClick={handlePaidConfirm}>
                <Check size={16} /> Ödendi Olarak İşaretle
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal && (
        <div className="wd-modal-overlay confirm" onClick={() => setShowConfirmModal(false)}>
          <div className="wd-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className={`wd-confirm-header ${confirmModalConfig.type}`}>
              <AlertTriangle size={24} />
              <h3>{confirmModalConfig.title}</h3>
            </div>
            <div className="wd-confirm-body">
              <p>{confirmModalConfig.message}</p>
            </div>
            <div className="wd-confirm-footer">
              <button className="wd-btn cancel" onClick={() => setShowConfirmModal(false)}>{confirmModalConfig.cancelText}</button>
              <button className={`wd-btn ${confirmModalConfig.type === 'danger' ? 'danger' : 'primary'}`} onClick={() => { setShowConfirmModal(false); confirmModalConfig.onConfirm(); }}>
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WholesalerDetail;
